'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter, useSearchParams } from 'next/navigation';
import qs from 'qs';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { SegmentPage } from '@flipster/features/src/misc/segment/segment-page';
import { Button } from '@flipster/react/design-system';
import { css } from '@flipster/styled-system/css';
import { FormItem, SessionInput } from '@flipster/ui';
import { SessionEvent } from '@flipster/utils/segment';
import { SessionStorageKey, setSessionStorage } from '@flipster/utils/sessionStorage';

import { ContinueWithApple } from '@web/app/[locale]/(web)/_auth/continue-with-apple';
import { ContinueWithGoogle } from '@web/app/[locale]/(web)/_auth/continue-with-google';
import { RegionBlocker } from '@web/hooks/useBlockedRegion';
import { SignUpSchema, signUpValidator } from '@web/utils/session-validator';

import { divider, formItem, h1, header, socialButtonGroup, wrapper } from '../_style';

const Page = () => {
  const router = useRouter();
  const searchParams = useSearchParams();

  const methods = useForm<SignUpSchema>({
    mode: 'onChange',
    resolver: yupResolver(signUpValidator),
  });

  const {
    handleSubmit,
    formState: { isValid, errors },
  } = methods;

  const onSubmitEmail = (data: SignUpSchema) => {
    const queryString = qs.stringify({ email: data.email });
    router.push(`/email-signup?${queryString}`);
  };

  useEffect(() => {
    const referralCode = searchParams.get('referral_code');
    const referralType = searchParams.get('referral_type');
    if (referralCode) setSessionStorage(SessionStorageKey.ReferralCode, referralCode);
    if (referralType && ['copy', 'share'].includes(referralType)) {
      setSessionStorage(SessionStorageKey.ReferralType, referralType);
    }
  }, [searchParams]);

  return (
    <RegionBlocker>
      <FormProvider {...methods}>
        <SegmentPage name={SessionEvent.CreateAccountPageView} context={{ type: 'root' }} />

        <form onSubmit={handleSubmit(onSubmitEmail)}>
          <div className={wrapper}>
            <header className={header}>
              <h1 className={h1}>Create account</h1>
            </header>
            <FormItem name='email' label='E-mail' error={errors.email?.message} className={formItem}>
              <SessionInput type='email' placeholder='name@flipster.io' />
            </FormItem>
            <div className={css({ mt: 24 })}>
              <Button type='submit' disabled={!isValid} label='Continue' size='large' css={{ w: '100%' }} />
            </div>
            <div className={divider}>OR</div>
            <div className={socialButtonGroup}>
              <ContinueWithGoogle label='Sign up with Google' />
              <ContinueWithApple label='Sign up with Apple' />
            </div>
          </div>
        </form>
      </FormProvider>
    </RegionBlocker>
  );
};

export default Page;
